import ChevronDown from '@/assets/images/ChevronDown.svg';
import ChevronUp from '@/assets/images/ChevronUp.svg';
import {
  BaseBrandedItem,
  GetBaseBrandedItemsQueryVariables,
  useGetBaseBrandedItemsQuery,
} from '@/graphql/types/generated';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { ErrorActions } from '@/state/errors';
import { itemsActions } from '@/state/baseBrandedItems';
import { useEffect, useState } from 'react';
import {
  Cell,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Box, Link, Spinner, ThemeUICSSObject } from 'theme-ui';
import { ItemColumns, itemTableColumns } from './ItemTableColumns';
import {
  cellStyles,
  headerCellStyles,
  headerStyles,
  indexTableWrapper,
  linkInheritStyles,
  rowStyles,
  spinnerStyle,
  tableStyles,
} from './ItemTableStyles';

const cellRenderer = (
  cell: Cell<ItemColumns, unknown>,
  style: ThemeUICSSObject,
  link?: string,
) => {
  const cellContents =
    link && cell.column.id !== 'edit' ? (
      <Link href={link} sx={linkInheritStyles}>
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </Link>
    ) : (
      flexRender(cell.column.columnDef.cell, cell.getContext())
    );

  return (
    <td key={cell.id} sx={style}>
      {cellContents}
    </td>
  );
};

const ItemTableContainer = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state) => state.baseBrandedItems.items);
  const [isRefreshing, setRefreshing] = useState(false);

  const filters = useAppSelector((state) => state.filters);

  const variables: GetBaseBrandedItemsQueryVariables = {
    warehouseId: filters.selectedWarehouseId,
    category: filters.selectedCategoryId,
    subcategory: filters.selectedSubcategoryId,
    printingMethod: filters.selectedBrandedPrintMethod,
    searchQuery: filters.searchQuery,
  };

  const [result] = useGetBaseBrandedItemsQuery({
    variables,
    requestPolicy: 'network-only', // TODO: figure out why it's not getting invoked with cache
  });

  const { fetching, error, data } = result;

  useEffect(() => {
    dispatch(ErrorActions.resetErrors());
    if (error) {
      dispatch(ErrorActions.setGraphqlErrors(error));
    }
  }, [error]);

  const table = useReactTable<ItemColumns>({
    columns: itemTableColumns,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    // Sorting is handled manually: v8 does not client-sort, and nothing here
    // dispatches a re-fetch on sort, so header toggles only drive the sort
    // indicator without reordering rows — matching the previous react-table
    // `manualSortBy` behavior.
    manualSorting: true,
  });

  useEffect(() => {
    if (fetching || !data?.baseBrandedItems) {
      return;
    }

    // At this point, we either have an array of Item or an empty array
    const items = data.baseBrandedItems as BaseBrandedItem[];

    if (isRefreshing) {
      dispatch(itemsActions.setItems(items));
      setRefreshing(false);
    } else {
      dispatch(itemsActions.appendItems(items));
    }
  }, [data?.baseBrandedItems, fetching]);

  useEffect(() => {
    if (data) {
      setRefreshing(true);
      dispatch(itemsActions.resetEndCursor());
    }
  }, [variables]);

  if (error) {
    return <b>There was an error fetching</b>;
  }

  return (
    <Box sx={indexTableWrapper}>
      {fetching ? <Spinner sx={spinnerStyle} /> : null}
      <table sx={tableStyles}>
        <thead sx={headerStyles}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  sx={{
                    ...headerCellStyles,
                    cursor: header.column.getCanSort() ? 'pointer' : undefined,
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  <span>
                    {header.column.getIsSorted() === 'desc' ? (
                      <img src={ChevronUp} />
                    ) : header.column.getIsSorted() === 'asc' ? (
                      <img src={ChevronDown} />
                    ) : (
                      ''
                    )}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const link = `/base_branded_item/item/${row.original.id}`;
            const cellStyle = Object.assign(cellStyles, {
              opacity: fetching ? 0.2 : 1,
            });
            return (
              <tr key={row.id} sx={rowStyles}>
                {row
                  .getVisibleCells()
                  .map((cell) => cellRenderer(cell, cellStyle, link))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

export default ItemTableContainer;
